export default {
  namespaced: true,

  getters: {
    type() {
      return (field) => {
        if (!field) {
          return false;
        }

        return field.type || null;
      };
    },

    some(state, getters, rootState, rootGetters) {
      return (field) => {
        const type = getters.type(field);

        if (!type) {
          return false;
        }

        const config = rootGetters['builder/config'];
        return (
          config.entities.fields.types[type] &&
          !!config.entities.fields.types[type].relation
        );
      };
    },

    keys(state, getters, rootState, rootGetters) {
      return (field) => {
        if (!getters.some(field)) {
          return [];
        }

        const config = rootGetters['builder/config'];
        const type = getters.type(field);

        if (
          config.entities.fields.types[type].relation ===
          true
        ) {
          return ['entity'];
        }

        return [
          'entity',
          ...config.entities.fields.types[type].relation,
        ];
      };
    },

    property(state, getters, rootState, rootGetters) {
      return (field, key) => {
        if (!getters.some(field)) {
          throw new Error(`Invalid property ${key}`);
        }

        if (key === 'entity') {
          return {
            required: true,
            input: 'entity',
            label: 'Entity',
          };
        }

        const config = rootGetters['builder/config'];
        const property =
          config.entities.fields['relation-properties'][
            key
          ];

        if (!property) {
          throw new Error(`Invalid property ${key}`);
        }

        return property;
      };
    },

    enabled(state, getters, rootState, rootGetters) {
      return (properties, entity, field, key) => {
        if (!getters.some(field)) {
          return false;
        }

        if (!key) {
          return true;
        }

        const property = getters.property(field, key);

        return rootGetters['builder/matcher/matches'](
          property.condition,
          properties,
          entity,
          field,
        );
      };
    },

    required(state, getters) {
      return (field, key) => {
        const property = getters.property(field, key);
        return !!property.required;
      };
    },

    input(state, getters) {
      return (field, key) => {
        const { input } = getters.property(field, key);

        if (!input) {
          return 'text';
        }

        return input.toLowerCase();
      };
    },

    htmlField(state, getters) {
      return (field, key) => {
        const input = getters.input(field, key);

        if (!input) {
          return 'text';
        }

        if (
          [
            'select',
            'entity',
            'field',
            'field_to_display',
          ].includes(input)
        ) {
          return 'select';
        }

        return input;
      };
    },

    label(state, getters) {
      return (field, key) => {
        const property = getters.property(field, key);
        return property.label;
      };
    },

    help(state, getters) {
      return (field, key) => {
        const property = getters.property(field, key);

        if (!property.help) {
          return null;
        }

        return property.help;
      };
    },

    relatedEntityName(
      state,
      getters,
      rootState,
      rootGetters,
    ) {
      return (field) => {
        if (
          !field ||
          !field.relation ||
          !field.relation.entity
        ) {
          return null;
        }

        const id = field.relation.entity;

        return rootGetters['builder/entityCurrent/get'](id)
          .name.singular;
      };
    },

    defaults(state, getters) {
      return (field) => {
        const keys = getters.keys(field);

        if (!keys.length) {
          return {};
        }

        const defaults = {};

        keys.forEach((key) => {
          const defaultValue = getters.defaultValue(
            field,
            key,
          );

          if (!defaultValue && defaultValue !== false) {
            return;
          }

          defaults[key] = defaultValue;
        });

        return defaults;
      };
    },

    defaultValue(state, getters) {
      return (field, key) => {
        const property = getters.property(field, key);
        return property.default;
      };
    },

    displayableValue(state, getters) {
      return (entities, entity, field, key, value) => {
        const htmlField = getters.htmlField(field, key);

        if (htmlField !== 'select') {
          return value;
        }

        const choices = getters.choices(
          entities,
          entity,
          field,
          key,
        );
        const choice = choices.find(
          (c) => c.value === value,
        );

        if (!choice) {
          return '';
        }

        return choice.label;
      };
    },

    choices(state, getters) {
      return (entities, entity, field, key) => {
        const input = getters.input(field, key);

        if (input === 'entity') {
          return getters.entityChoices(entities, entity);
        }

        if (input === 'field') {
          return getters.fieldChoices(entities, field);
        }

        if (input === 'field_to_display') {
          return getters.fieldChoicesToDisplay(
            entities,
            field,
          );
        }

        return getters.propertyChoices(field, key);
      };
    },

    entityChoices() {
      return (entities, entity) => {
        const choices = [];

        if (entity) {
          choices.push({
            value: entity.id,
            label:
              entity.name && entity.name.singular
                ? entity.name.singular
                : 'this',
          });
        }

        if (!entities || !entities.length) {
          return choices;
        }

        return [
          ...choices,
          ...entities
            .filter(
              (filteredEntity) =>
                filteredEntity.id !== (entity && entity.id),
            )
            .map((mappedEntity) => ({
              value: mappedEntity.id,
              label: mappedEntity.name.singular,
            })),
        ];
      };
    },

    fieldChoices() {
      return (entities, field) => {
        if (!field.relation.entity) {
          return [];
        }

        const relatedEntity = entities.find(
          (e) => e.id === field.relation.entity,
        );

        if (!relatedEntity) {
          return [];
        }

        const { fields } = relatedEntity;

        if (!fields || !fields.length) {
          return [];
        }

        return fields.map((mappedField) => ({
          value: mappedField.id,
          label: mappedField.name.singular,
        }));
      };
    },

    fieldChoicesToDisplay() {
      return (entities, field) => {
        const output = [
          {
            value: 'id',
            label: 'id',
          },
        ];

        if (!field.relation.entity) {
          return output;
        }

        const relatedEntity = entities.find(
          (e) => e.id === field.relation.entity,
        );

        if (!relatedEntity) {
          return output;
        }

        const { fields } = relatedEntity;

        if (!fields || !fields.length) {
          return output;
        }

        return [
          ...output,
          ...fields
            .filter((field) => {
              return [
                'text',
                'integer',
                'decimal',
                'enumerator',
              ].includes(field.type);
            })
            .map((mappedField) => ({
              value: mappedField.id,
              label: mappedField.name.singular,
            })),
        ];
      };
    },

    propertyChoices(state, getters) {
      return (field, key) => {
        const { choices } = getters.property(field, key);

        if (!choices || !Object.keys(choices).length) {
          return [];
        }

        return Object.keys(choices).map((choiceKey) => ({
          value: choiceKey,
          label: choices[choiceKey].label,
        }));
      };
    },
  },
};
